// PerformanceIndicator.tsx
import React, { useState, useEffect } from 'react';
import { Box, Tooltip, keyframes } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import { performanceStore } from '../utils/performanceMetricsStore';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';

const slideInFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(400px);
    z-index: 0;
  }
  to {
    opacity: 1;
    transform: translateY(0);
    z-index: 40;
  }
`;

const slideOutFadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
    z-index: 40;
  }
  to {
    opacity: 0;
    transform: translateY(40px);
    z-index: 0;
  }
`;

const PerformanceIndicator: React.FC = () => {
  const theme = useTheme();
  const { user, isAuthenticated } = useAuth();
  const [stats, setStats] = useState(performanceStore.getStats());
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  // Per-user preference (see AuthContext.User / ProfilePage), read straight
  // off the authenticated user - no fetch or event listener needed.
  const showIndicator = isAuthenticated
    ? (user?.showPerformanceIndicator ?? true)
    : null;

  useEffect(() => {
    const unsubscribe = performanceStore.subscribe(() => {
      setStats(performanceStore.getStats());
      setIsAnimatingOut(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (stats.totalRequests === 0 || !showIndicator) return;

    const timer = setTimeout(() => {
      setIsAnimatingOut(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, [stats, showIndicator]);

  if (showIndicator === null || stats.totalRequests === 0 || !showIndicator)
    return null;

  const avgTime = stats.averageResponseTime;
  const getColor = () => {
    if (avgTime < 200) return theme.palette.success.main;
    if (avgTime < 500) return theme.palette.info.main;
    return theme.palette.warning.main;
  };

  const getLabel = () => {
    if (avgTime < 200) return 'Good';
    if (avgTime < 500) return 'Fair';
    return 'Slow';
  };

  return (
    <Tooltip
      title={`Avg: ${avgTime}ms | Slow: ${stats.slowRequests} | Total: ${stats.totalRequests}`}
    >
      <Box
        sx={{
          position: 'fixed',
          bottom: '20px',
          left: '20px',
          padding: '12px 16px',
          backgroundColor: getColor(),
          color:
            theme.palette.mode === 'dark'
              ? theme.palette.grey[50]
              : theme.palette.grey[100],
          borderRadius: '8px',
          fontSize: '1.6rem',
          fontWeight: 700,
          border: `2px solid ${theme.palette.grey[50]}`,
          zIndex: 0,
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          animation: isAnimatingOut
            ? `${slideOutFadeOut} 300ms ease-in forwards`
            : `${slideInFadeIn} 300ms ease-out forwards`,
        }}
      >
        <BoltIcon sx={{ fontSize: '1.4em' }} />
        <span>{getLabel()}</span>
        <span>{avgTime}ms</span>
      </Box>
    </Tooltip>
  );
};

export default PerformanceIndicator;
